// Services.tsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Tabs,
  Tab,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  Divider,
  Card,
  CardContent,
  Stack,
  OutlinedInput,
  Box as MuiBox,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

// Adjust these imports to your project structure
import { Service, ServiceCategory, CustomerRequirement } from '../types/service';
import { serviceCategoriesAPI, servicesAPI } from '../services/api';

/* ============================
   Types used in this file
   ============================ */

interface ServiceFormData {
  service_name: string;
  description?: string;
  category_id: string;      // stored as string in the form (select value)
  base_price: string;      // keep as string to match TextField value
  duration_minutes: string;
  is_active: boolean;
  requirements: Array<{ requirement_id: number; is_mandatory?: boolean }>;
  equipment: number[];     // numeric ids
  addons: number[];        // numeric ids (we store as numbers here)
  images: File[];          // files to upload
}

/* Validation Schema (Yup) */
const serviceSchema = yup.object({
  service_name: yup.string().required('Service name is required'),
  category_id: yup.string().required('Category is required'),
  base_price: yup.string().required('Price is required'),
  duration_minutes: yup.string().required('Duration is required'),
  is_active: yup.boolean().required(),
  addons: yup.array().of(yup.number()).optional(),
  requirements: yup.array().of(
    yup.object({
      requirement_id: yup.number().required(),
      is_mandatory: yup.boolean().optional(),
    })
  ),
});

/* Helper: TabPanel */
interface TabPanelProps { children?: React.ReactNode; index: number; value: number; }
function TabPanel({ children, value, index, ...other }: TabPanelProps) {
  return (
    <div role="tabpanel" hidden={value !== index} id={`service-tabpanel-${index}`} aria-labelledby={`service-tab-${index}`} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

/* Component: Services */
const Services: React.FC = () => {
  const [tabValue, setTabValue] = useState<number>(0);

  /* Categories */
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [openCategoryDialog, setOpenCategoryDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState<ServiceCategory | null>(null);
  const [categoryFormData, setCategoryFormData] = useState({ name: '', description: '', image_url: '', status: true, display_order: 0 });

  /* Services list */
  const [services, setServices] = useState<Service[]>([]);

  /* Available resources */
  const [availableRequirements, setAvailableRequirements] = useState<CustomerRequirement[]>([]);
  const [availableEquipment, setAvailableEquipment] = useState<any[]>([]);
  const [availableAddons, setAvailableAddons] = useState<any[]>([]); // from API addon_id is number

  /* Dialogs */
  const [openServiceDialog, setOpenServiceDialog] = useState(false);
  const [editingService, setEditingService] = useState<Service | null>(null);

  /* UI states */
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  /* Default form values */
  const defaultFormValues: ServiceFormData = {
    service_name: '',
    description: '',
    category_id: '',
    base_price: '',
    duration_minutes: '30',
    is_active: true,
    requirements: [],
    equipment: [],
    addons: [],
    images: [],
  };

  /* React Hook Form setup */
  const { control, handleSubmit, reset, watch, setValue } = useForm<ServiceFormData>({
    defaultValues: defaultFormValues,
    // cast resolver to any to avoid strict generic mismatch between yup and useForm generics
    resolver: yupResolver(serviceSchema) as any,
  });

  /* Watchers */
  const watchedAddons = watch('addons', []);
  const watchedImages = watch('images', []);

  /* Fetch initial data */
  useEffect(() => {
    fetchInitial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchInitial = async () => {
    setLoading(true);
    try {
      await Promise.all([fetchCategories(), fetchServices(), fetchAvailableResources()]);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };
  const handleCloseSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

  /* API fetchers */
  const fetchCategories = async () => {
    try {
      const resp = await serviceCategoriesAPI.getAll({ status: true });
      setCategories(Array.isArray(resp) ? resp : []);
    } catch (err) {
      console.error('fetchCategories', err);
      showSnackbar('Failed to load categories', 'error');
    }
  };

  const fetchServices = async () => {
    try {
      const resp = await servicesAPI.getAll();
      setServices(Array.isArray(resp) ? resp : []);
    } catch (err) {
      console.error('fetchServices', err);
    }
  };

  const fetchAvailableResources = async () => {
    try {
      const [reqs, equip, addons] = await Promise.all([
        servicesAPI.getAvailableRequirements(),
        servicesAPI.getAvailableEquipment(),
        servicesAPI.getAvailableAddons(),
      ]);
      setAvailableRequirements(Array.isArray(reqs) ? reqs : []);
      setAvailableEquipment(Array.isArray(equip) ? equip : []);
      setAvailableAddons(Array.isArray(addons) ? addons : []);
    } catch (err) {
      console.error('fetchAvailableResources', err);
    }
  };

  /* Category handlers */
  const handleOpenCategoryDialog = (category: ServiceCategory | null = null) => {
    if (category) {
      setEditingCategory(category);
      setCategoryFormData({
        name: category.name,
        description: category.description || '',
        image_url: category.image_url || '',
        status: category.status,
        display_order: category.display_order || 0,
      });
    } else {
      setEditingCategory(null);
      setCategoryFormData({ name: '', description: '', image_url: '', status: true, display_order: categories.length + 1 });
    }
    setOpenCategoryDialog(true);
  };
  const handleCloseCategoryDialog = () => { setOpenCategoryDialog(false); setEditingCategory(null); };
  const handleCategoryInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCategoryFormData((prev) => ({ ...prev, [name]: name === 'display_order' ? parseInt(value || '0', 10) : value }));
  };
  const handleCategorySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      if (editingCategory) {
        await serviceCategoriesAPI.update(editingCategory.category_id, categoryFormData);
        showSnackbar('Category updated', 'success');
      } else {
        await serviceCategoriesAPI.create(categoryFormData);
        showSnackbar('Category created', 'success');
      }
      await fetchCategories();
      handleCloseCategoryDialog();
    } catch (err) {
      console.error('handleCategorySubmit', err);
      showSnackbar('Failed to save category', 'error');
    } finally {
      setLoading(false);
    }
  };
  const handleDeleteCategory = async (id: number) => {
    if (!confirm('Delete category?')) return;
    try {
      setLoading(true);
      await serviceCategoriesAPI.delete(id);
      showSnackbar('Category deleted', 'success');
      await fetchCategories();
    } catch (err) {
      console.error('deleteCategory', err);
      showSnackbar('Failed to delete', 'error');
    } finally {
      setLoading(false);
    }
  };
  const toggleCategoryStatus = async (id: number) => {
    try {
      setLoading(true);
      const updated = await serviceCategoriesAPI.toggleStatus(id);
      setCategories((prev) => prev.map((c) => (c.category_id === id ? updated : c)));
      showSnackbar('Category status updated', 'success');
    } catch (err) {
      console.error('toggleCategoryStatus', err);
      showSnackbar('Failed to update', 'error');
    } finally {
      setLoading(false);
    }
  };

  /* Service dialog open/edit */
  const handleOpenServiceDialog = (service: Service | null = null) => {
    setEditingService(service);
    if (!service) {
      reset({ ...defaultFormValues, category_id: categories[0]?.category_id?.toString() || '' });
      setOpenServiceDialog(true);
      return;
    }

    reset({
      service_name: service.name,
      description: service.description || '',
      category_id: String(service.category_id ?? ''),
      base_price: String(service.base_price ?? ''),
      duration_minutes: String(service.duration ?? '30'),
      is_active: !!service.is_active,
      requirements: [],
      equipment: [],
      addons: [],
      images: [],
    });

    setOpenServiceDialog(true);
    (async () => {
      try {
        setLoading(true);
        const complete = await servicesAPI.getCompleteService(service.service_id);
        const reqs = Array.isArray(complete.requirements) ? complete.requirements.map((r: any) => ({ requirement_id: r.requirement_id, is_mandatory: !!r.is_mandatory })) : [];
        const equipment = Array.isArray(complete.equipment) ? complete.equipment.map((e: any) => e.equipment_id) : [];
        const addons = Array.isArray(complete.addons) ? complete.addons.map((a: any) => a.addon_id) : [];

        setValue('requirements', reqs);
        setValue('equipment', equipment);
        setValue('addons', addons);
        setValue('images', []);
      } catch (err) {
        console.error('getCompleteService', err);
        showSnackbar('Failed to load service details', 'error');
      } finally {
        setLoading(false);
      }
    })();
  };

  const handleCloseServiceDialog = () => {
    setOpenServiceDialog(false);
    setEditingService(null);
    reset(defaultFormValues);
  };

  /* Requirements / Equipment / Addons toggles */
  const toggleRequirement = (requirementId: number, checked: boolean) => {
    const current = (watch('requirements') as any[]) || [];
    if (checked) {
      if (!current.find((r) => r.requirement_id === requirementId)) {
        setValue('requirements', [...current, { requirement_id: requirementId, is_mandatory: false }]);
      }
    } else {
      setValue('requirements', current.filter((r) => r.requirement_id !== requirementId));
    }
  };
  const toggleRequirementMandatory = (requirementId: number, isMandatory: boolean) => {
    const current = (watch('requirements') as any[]) || [];
    setValue('requirements', current.map((r) => (r.requirement_id === requirementId ? { ...r, is_mandatory: isMandatory } : r)));
  };
  const toggleEquipment = (equipmentId: number) => {
    const current = (watch('equipment') as number[]) || [];
    if (current.includes(equipmentId)) setValue('equipment', current.filter((id) => id !== equipmentId));
    else setValue('equipment', [...current, equipmentId]);
  };

  /* Addon helpers: store as number[] in form */
  const toggleAddon = (addonId: number) => {
    const current = (watch('addons') as number[]) || [];
    if (current.includes(addonId)) setValue('addons', current.filter((id) => id !== addonId));
    else setValue('addons', [...current, addonId]);
  };
  const selectAllAddons = () => setValue('addons', availableAddons.map((a: any) => a.addon_id));
  const deselectAllAddons = () => setValue('addons', []);

  /* Images */
  const handleImageFiles = (files: FileList | null) => {
    if (!files) return;
    const arr = Array.from(files);
    setValue('images', [...((watch('images') as File[]) || []), ...arr]);
  };
  const removeImageAt = (index: number) => {
    const current = (watch('images') as File[]) || [];
    setValue('images', current.filter((_, i) => i !== index));
  };

  /* Submit handler */
  const onSubmit: SubmitHandler<ServiceFormData> = async (data) => {
    try {
      setLoading(true);

      const payload = {
        service_name: data.service_name,
        description: data.description,
        base_price: parseFloat(data.base_price) || 0,
        duration_minutes: parseInt(data.duration_minutes, 10) || 30,
        category_id: parseInt(data.category_id, 10) || 0,
        is_active: !!data.is_active,
        requirements: data.requirements || [],
        equipment: (data.equipment || []).map((id) => ({ equipment_id: id })),
        addons: (data.addons || []).map((id) => ({ addon_id: id })), // already numbers
      };

      if (editingService) {
        await servicesAPI.update(editingService.service_id, payload);
        showSnackbar('Service updated', 'success');

        if ((data.images || []).length > 0) {
          try {
            await servicesAPI.uploadImages(editingService.service_id, data.images);
          } catch (err) {
            console.error('uploadImages (update)', err);
            showSnackbar('Service saved but failed to upload images', 'error');
          }
        }
      } else {
        const created = await servicesAPI.create(payload);
        showSnackbar('Service created', 'success');

        if ((data.images || []).length > 0 && created?.service_id) {
          try {
            await servicesAPI.uploadImages(created.service_id, data.images);
          } catch (err) {
            console.error('uploadImages (create)', err);
            showSnackbar('Created but failed to upload images', 'error');
          }
        }
      }

      await fetchServices();
      handleCloseServiceDialog();
    } catch (err) {
      console.error('onSubmit', err);
      showSnackbar('Failed to save service', 'error');
    } finally {
      setLoading(false);
    }
  };

  /* Delete / Toggle service status */
  const handleDeleteService = async (id: number) => {
    if (!confirm('Delete service?')) return;
    try {
      setLoading(true);
      await servicesAPI.delete(id);
      showSnackbar('Service deleted', 'success');
      await fetchServices();
    } catch (err) {
      console.error('deleteService', err);
      showSnackbar('Failed to delete service', 'error');
    } finally {
      setLoading(false);
    }
  };
  const toggleServiceStatus = async (id: number) => {
    const svc = services.find((s) => s.service_id === id);
    if (!svc) return;
    try {
      setLoading(true);
      await servicesAPI.update(id, {
        service_name: svc.name,
        description: svc.description,
        base_price: svc.base_price,
        duration_minutes: svc.duration,
        category_id: svc.category_id,
        is_active: !svc.is_active,
      });
      showSnackbar('Service status updated', 'success');
      await fetchServices();
    } catch (err) {
      console.error('toggleServiceStatus', err);
      showSnackbar('Failed to update status', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getCategoryName = (categoryId: number) => categories.find((c) => c.category_id === categoryId)?.name || 'Unknown';

  /* Addons normalized for render (keep original addon_id number) */
  const addonsForRender = useMemo(() => {
    return availableAddons.map((a: any) => ({
      addon_id: a.addon_id as number,
      name: a.name,
      description: a.description,
      base_price: parseFloat(a.base_price || 0),
    }));
  }, [availableAddons]);

  /* Render */
  return (
    <Box sx={{ p: 3}}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        
        <Typography variant="h4" gutterBottom>
          Service Categories
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenCategoryDialog(null)} disabled={loading}>Add Category</Button>
     </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      
        <Paper sx={{ p: 0 }}>
          <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 1 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {categories.length === 0 ? (
                  <TableRow><TableCell colSpan={6} align="center">No categories found.</TableCell></TableRow>
                ) : categories.map((c) => (
                  <TableRow key={c.category_id} hover>
                    <TableCell>{c.name}</TableCell>
                    <TableCell>{c.description || '-'}</TableCell>
                    <TableCell>
                      <Chip label={c.status ? 'Active' : 'Inactive'} color={c.status ? 'success' : 'default'} size="small" onClick={() => toggleCategoryStatus(c.category_id)} sx={{ cursor: 'pointer' }} />
                    </TableCell>
                    <TableCell>
                      <IconButton onClick={() => handleOpenCategoryDialog(c)} size="small" disabled={loading}><EditIcon fontSize="small" /></IconButton>
                      <IconButton onClick={() => handleDeleteCategory(c.category_id)} color="error" size="small" disabled={loading}><DeleteIcon fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
     
        
      {/* Category Dialog */}
      <Dialog open={openCategoryDialog} onClose={handleCloseCategoryDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingCategory ? 'Edit Category' : 'Add Category'}</DialogTitle>
        <form onSubmit={handleCategorySubmit}>
          <DialogContent>
            <TextField label="Name" name="name" value={categoryFormData.name} onChange={handleCategoryInputChange} fullWidth margin="normal" required />
            <TextField label="Description" name="description" value={categoryFormData.description} onChange={handleCategoryInputChange} fullWidth margin="normal" multiline rows={2} />
            <TextField label="Image URL" name="image_url" value={categoryFormData.image_url} onChange={handleCategoryInputChange} fullWidth margin="normal" />
            <TextField label="Display Order" name="display_order" value={categoryFormData.display_order} onChange={handleCategoryInputChange} fullWidth margin="normal" type="number" required />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseCategoryDialog}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={loading}>{editingCategory ? 'Update' : 'Add'}</Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Service Dialog */}
      <Dialog open={openServiceDialog} onClose={handleCloseServiceDialog} maxWidth="lg" fullWidth>
        <DialogTitle>{editingService ? 'Edit Service' : 'Add Service'}</DialogTitle>

        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent>
            <Grid container spacing={2}>
              {/* Category */}
              <Grid item xs={12} sm={6}>
                <Controller name="category_id" control={control} render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Category *</InputLabel>
                    <Select {...field} label="Category *" input={<OutlinedInput label="Category *" />}>
                      {categories.filter(c => c.status).map((cat) => <MenuItem key={cat.category_id} value={String(cat.category_id)}>{cat.name}</MenuItem>)}
                    </Select>
                  </FormControl>
                )} />
              </Grid>

              {/* Service name */}
              <Grid item xs={12} sm={6}>
                <Controller name="service_name" control={control} render={({ field }) => (<TextField {...field} label="Service Name" fullWidth required />)} />
              </Grid>

              {/* Description */}
              <Grid item xs={12}>
                <Controller name="description" control={control} render={({ field }) => (<TextField {...field} label="Description" fullWidth multiline rows={2} />)} />
              </Grid>

              {/* Duration */}
              <Grid item xs={12} sm={4}>
                <Controller name="duration_minutes" control={control} render={({ field }) => (<TextField {...field} label="Duration (minutes)" type="number" fullWidth required />)} />
              </Grid>

              {/* Price */}
              <Grid item xs={12} sm={4}>
                <Controller name="base_price" control={control} render={({ field }) => (<TextField {...field} label="Price ($)" type="number" inputProps={{ step: '0.01' }} fullWidth required />)} />
              </Grid>

              {/* Status */}
              <Grid item xs={12} sm={4}>
                <Controller name="is_active" control={control} render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select {...field} value={field.value ? 'true' : 'false'} onChange={(e) => field.onChange(e.target.value === 'true')}>
                      <MenuItem value="true">Active</MenuItem>
                      <MenuItem value="false">Inactive</MenuItem>
                    </Select>
                  </FormControl>
                )} />
              </Grid>

              {/* Requirements */}
              <Grid item xs={12}>
                <Typography variant="h6">Customer Requirements</Typography>
                <Typography variant="body2" color="text.secondary">Select requirements customers must meet</Typography>
                <Divider sx={{ mb: 2 }} />
                <Card variant="outlined"><CardContent>
                  <List>
                    {availableRequirements.map((req) => {
                      const selected = (watch('requirements') as any[] || []).some((r) => r.requirement_id === req.requirement_id);
                      const mandatory = (watch('requirements') as any[] || []).find((r) => r.requirement_id === req.requirement_id)?.is_mandatory || false;
                      return (
                        <ListItem key={req.requirement_id} divider secondaryAction={
                          <Stack direction="row" spacing={1}>
                            <FormControlLabel control={<Checkbox checked={selected} onChange={(e) => toggleRequirement(req.requirement_id, e.target.checked)} />} label="Required" />
                            {selected && <FormControlLabel control={<Checkbox checked={mandatory} onChange={(e) => toggleRequirementMandatory(req.requirement_id, e.target.checked)} />} label="Mandatory" />}
                          </Stack>
                        }>
                          <ListItemText primary={req.name} secondary={req.description} />
                        </ListItem>
                      );
                    })}
                  </List>
                </CardContent></Card>
              </Grid>

              {/* Equipment */}
              <Grid item xs={12}>
                <Typography variant="h6">Cleaning Equipment</Typography>
                <Typography variant="body2" color="text.secondary">Select required equipment</Typography>
                <Divider sx={{ mb: 2 }} />
                <Card variant="outlined"><CardContent>
                  <Grid container spacing={2}>
                    {availableEquipment.map((eq: any) => (
                      <Grid item xs={12} sm={6} md={4} key={eq.equipment_id}>
                        <FormControlLabel control={<Checkbox checked={(watch('equipment') as number[] || []).includes(eq.equipment_id)} onChange={() => toggleEquipment(eq.equipment_id)} />} label={<MuiBox><Typography fontWeight={500}>{eq.name}</Typography>{eq.description && <Typography variant="caption" color="text.secondary">{eq.description}</Typography>}</MuiBox>} />
                      </Grid>
                    ))}
                  </Grid>
                </CardContent></Card>
              </Grid>

              {/* Addons (Autocomplete multiple) */}
              <Grid item xs={12}>
                <Typography variant="h6">Addon Services</Typography>
                <Typography variant="body2" color="text.secondary">Select addon services to bundle with this service</Typography>
                <Divider sx={{ mb: 2 }} />
                <Card variant="outlined"><CardContent>
                  <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                    <Button size="small" onClick={selectAllAddons}>Select All</Button>
                    <Button size="small" onClick={deselectAllAddons}>Deselect All</Button>
                  </Stack>

                  <Controller
                    name="addons"
                    control={control}
                    render={({ field }) => (
                      <Autocomplete
                        multiple
                        options={addonsForRender}
                        disableCloseOnSelect
                        getOptionLabel={(option: any) => `${option.name} ($${option.base_price})`}
                        value={addonsForRender.filter((addon) => (field.value || []).includes(addon.addon_id))}
                        onChange={(_, newValue) => field.onChange(newValue.map((addon: any) => addon.addon_id))}
                        renderOption={(props, option, { selected }) => (
                          <li {...props}>
                            <Checkbox style={{ marginRight: 8 }} checked={selected} />
                            <MuiBox>
                              <Typography variant="body2" fontWeight={600}>{option.name}</Typography>
                              <Typography variant="caption" color="text.secondary">${option.base_price}</Typography>
                            </MuiBox>
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField {...params} label="Available Addon Services" placeholder="Select addon services" />
                        )}
                      />
                    )}
                  />

                </CardContent></Card>
              </Grid>

              {/* Images */}
              <Grid item xs={12}>
                <Typography variant="h6">Service Images</Typography>
                <Typography variant="body2" color="text.secondary">Upload images (optional)</Typography>
                <Divider sx={{ mb: 2 }} />
                <Box>
                  <input accept="image/*" style={{ display: 'none' }} id="service-images-input" type="file" multiple onChange={(e) => handleImageFiles(e.target.files)} />
                  <label htmlFor="service-images-input"><Button component="span">Choose Images</Button></label>

                  {(watch('images') as File[] || []).length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2">Selected Images ({(watch('images') as File[]).length})</Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {(watch('images') as File[] || []).map((f, i) => <Chip key={i} label={`${f.name} (${(f.size / 1024 / 1024).toFixed(2)} MB)`} onDelete={() => removeImageAt(i)} />)}
                      </Stack>
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions>
            <Button onClick={handleCloseServiceDialog} disabled={loading}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={loading}>{editingService ? 'Update Service' : 'Add Service'}</Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled">{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

/* Helper: getCategoryName uses categories state via closure below */
function getCategoryName(categoryId: number | undefined) {
  // Note: this helper can't access state directly here; using the global categories variable would be wrong.
  // The component uses an inline version above when rendering. Keep this fallback:
  return 'Unknown';
}

export default Services;
